import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useShallow } from "zustand/shallow";
import { useEditorStore, useRectificationStore } from "../../../stores";
import { CreateSmoothingPreviewMaterial, CreateRectificationZonePreviewMaterial } from "../../../utils/PreviewMaterialsHandler";
import { MeshBVH, acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
import { buildAdjacency, detectBoundaryVertices } from "./helpers";

THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

const CM_TO_UNIT = 1; // keep units consistent with scene

const SmootheningHandler = () => {
    const [scene, shadedMesh, material, controls, camera, renderer, setInteractiveObjects, setPaintingCursor] = useEditorStore(
        useShallow((s) => {
            const refs = s.sceneRefs || {};
            return [refs.scene, refs.shadedMesh, refs.material, refs.controls, refs.camera, refs.renderer, s.setInteractiveObjects, s.setPaintingCursor];
        })
    );

    const [smoothning, setSmoothning] = useRectificationStore(
        useShallow((s) => [s.smoothning, s.setSmoothning])
    );

    // ---- Refs / caches ------------------------------------------------------
    const overlayMeshRef = useRef({}); // Colored overlay meshes per zone
    const paintingRef = useRef(false);
    const brushRadiusRef = useRef(smoothning?.brushRadius ?? 1);
    const lastPaintPosRef = useRef(null); // Track last paint position for interpolation
    const lastGeometryRef = useRef(null); // track geometry changes for overlay invalidation

    useEffect(() => {
        brushRadiusRef.current = smoothning?.brushRadius ?? 1;
    }, [smoothning?.brushRadius]);

    // Helper: the selected zone is the expanded one; fallback to first
    function getSelectedZoneId() {
        const zones = useRectificationStore.getState().smoothning?.zones || [];
        const expanded = zones.find((z) => z.isExpanded);
        return expanded?.id || (zones[0]?.id ?? 0);
    }

    // ---- Overlays -----------------------------------------------------------
    function ensureOverlay() {
        if (!shadedMesh) return;
        const zones = useRectificationStore.getState().smoothning?.zones || [];
        for (const z of zones) ensureZoneOverlay(z.id);
    }

    function ensureZoneOverlay(zoneId) {
        // Check if geometry has changed (e.g., after alignment transformation)
        const geometryChanged = lastGeometryRef.current &&
            shadedMesh &&
            shadedMesh.geometry.attributes.position !== lastGeometryRef.current;

        // Invalidate all cached overlays if geometry changed
        if (geometryChanged) {
            Object.values(overlayMeshRef.current).forEach(overlay => {
                if (overlay && overlay.parent) {
                    overlay.parent.remove(overlay);
                }
                overlay?.geometry?.dispose();
                overlay?.material?.dispose();
            });
            overlayMeshRef.current = {};
        }

        if (overlayMeshRef.current[zoneId]) return overlayMeshRef.current[zoneId];
        if (!shadedMesh) return null;

        const base = shadedMesh.geometry;
        lastGeometryRef.current = base.attributes.position; // track current geometry
        const overlayGeom = new THREE.BufferGeometry();
        if (base.index) overlayGeom.setIndex(base.index);
        overlayGeom.setAttribute("position", base.attributes.position);
        overlayGeom.setAttribute("normal", base.attributes.normal);

        const count = base.attributes.position.count;
        const paint = new Float32Array(count);
        overlayGeom.setAttribute("paint", new THREE.BufferAttribute(paint, 1));

        const mat = new THREE.ShaderMaterial({
            uniforms: {
                uColor: { value: new THREE.Color(1, 1, 1) },
            },
            vertexShader: `
                precision highp float;
                attribute float paint;
                varying float vPaint;
                void main() {
                    vPaint = paint;
                    vec3 pos = position + normal * 0.0006;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
                }
            `,
            fragmentShader: `
                precision highp float;
                uniform vec3 uColor;
                varying float vPaint;
                void main() {
                    float alpha = smoothstep(0.48, 0.52, vPaint);
                    if (alpha < 0.01) discard;
                    vec3 col = pow(clamp(uColor, 0.0, 1.0), vec3(0.5));
                    gl_FragColor = vec4(col, alpha);
                    #include <colorspace_fragment>
                    #include <dithering_fragment>
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
            depthTest: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2.1,
        });

        const overlay = new THREE.Mesh(overlayGeom, mat);
        overlay.name = `SmootheningOverlay_${zoneId}`;
        overlay.renderOrder = 5e5 + zoneId;
        if (shadedMesh) shadedMesh.add(overlay);
        overlayMeshRef.current[zoneId] = overlay;
        return overlay;
    }

    // ---- Preview Mesh in Store ---------------------------------------------
    function ensurePreview() {
        const latest = useRectificationStore.getState().smoothning;
        let mesh = latest.previewMesh;
        if (mesh && mesh.isMesh) {
            if (!mesh.parent) scene.add(mesh);
            return mesh;
        }
        if (!shadedMesh) return null;

        const base = shadedMesh.geometry.clone();
        base.computeVertexNormals();
        const work = base.clone();

        const mat = CreateSmoothingPreviewMaterial(material);
        mesh = new THREE.Mesh(work, mat);
        mesh.name = "SmootheningPreview";
        mesh.castShadow = shadedMesh.castShadow; mesh.receiveShadow = shadedMesh.receiveShadow;
        mesh.matrixAutoUpdate = true;
        mesh.position.copy(shadedMesh.position); mesh.quaternion.copy(shadedMesh.quaternion); mesh.scale.copy(shadedMesh.scale);
        mesh.visible = (useRectificationStore.getState().smoothning?.previewVisible ?? true);

        // Persist working data
        mesh.userData.baseGeometry = base;
        mesh.userData.basePos = base.attributes.position.array.slice(0);
        base.computeVertexNormals();
        mesh.userData.baseNor = base.attributes.normal.array.slice(0);
        mesh.userData.accumShift = new Float32Array(work.attributes.position.count);
        // adjacency for smoothing
        mesh.userData.neighbors = buildAdjacency(base);
        // detect and store boundary vertices
        mesh.userData.isBoundary = detectBoundaryVertices(base);

        scene.add(mesh);
        setSmoothning({ previewMesh: mesh });
        return mesh;
    }

    function disposePreviewFromStore() {
        const latest = useRectificationStore.getState().smoothning;
        const mesh = latest.previewMesh;
        if (mesh && mesh.isMesh) {
            mesh.parent?.remove(mesh);
            mesh.geometry?.dispose?.();
            mesh.material?.dispose?.();
            mesh.userData?.baseGeometry?.dispose?.();
        }
        setSmoothning({ previewMesh: null });
    }

    // ---- Painting -----------------------------------------------------------
    function paintAt(hitPointWorld, isDragging = false) {
        if (!shadedMesh) return;
        const latest = useRectificationStore.getState().smoothning;
        const zones = latest?.zones || [];
        const currentSelectedZoneId = getSelectedZoneId();
        const zone = zones.find((z) => z.id === currentSelectedZoneId);
        if (!zone) return;

        const baseGeom = latest.previewMesh?.userData?.baseGeometry || shadedMesh.geometry;
        const basePos = baseGeom.attributes.position;
        const vertexCount = basePos.count;

        if (!zone.influences || zone.influences.length !== vertexCount) {
            zone.influences = new Float32Array(vertexCount);
        }

        const brushRadiusWorld = (brushRadiusRef.current ?? 1) * CM_TO_UNIT;
    
        // Interpolate between last and current position when dragging
        const strokePositions = [];
        
        if (isDragging && lastPaintPosRef.current) {
        // Calculate distance between last and current position
        const lastPos = lastPaintPosRef.current;
        const distance = lastPos.distanceTo(hitPointWorld);
        
        // Determine number of interpolation steps based on distance
        // More steps for longer distances to ensure smooth coverage
        const stepSize = brushRadiusWorld * 0.2; // Step at 20% of brush radius for good overlap
        const numSteps = Math.max(1, Math.ceil(distance / stepSize));
        
        // Interpolate positions
        for (let step = 0; step <= numSteps; step++) {
            const t = step / numSteps;
            const interpPos = new THREE.Vector3().lerpVectors(lastPos, hitPointWorld, t);
            strokePositions.push(interpPos);
        }
        } else {
        // Single click or first drag point
        strokePositions.push(hitPointWorld);
        }
        
        // Store current position for next iteration
        lastPaintPosRef.current = hitPointWorld.clone();
        
        // Apply paint at each interpolated position
        let influencesChanged = false;
        const brushRadiusSq = brushRadiusWorld * brushRadiusWorld;
        
        // Adjust accumulation strength based on number of positions
        // This prevents over-accumulation when many interpolated positions overlap
        const accumulationStrength = isDragging ? 0.5 / Math.sqrt(strokePositions.length) : 0.5;
        
        for (const strokePos of strokePositions) {
            // Convert stroke position to local space
            const strokeLocal = new THREE.Vector3().copy(strokePos);
            shadedMesh.worldToLocal(strokeLocal);
            
            // Paint at this position
            for (let i = 0; i < vertexCount; i++) {
                const bx = basePos.getX(i), by = basePos.getY(i), bz = basePos.getZ(i);
                
                const dx = bx - strokeLocal.x;
                const dy = by - strokeLocal.y;
                const dz = bz - strokeLocal.z;
                
                const d2 = dx*dx + dy*dy + dz*dz;
                if (d2 >= brushRadiusSq) continue; // outside brush, skip
                
                // Smoother falloff function for better edges
                const nd2 = d2 / brushRadiusSq;     // (d/r)^2
                const t   = 1.0 - nd2;              // 1 - (d/r)^2
                
                // Use a smoother cubic falloff instead of quartic for softer edges
                const falloff = t * t * (3.0 - 2.0 * t); // Smoothstep function
                const add = falloff * accumulationStrength;
                
                // Use max operation instead of pure addition for more consistent results
                const old = zone.influences[i];
                const nu  = Math.max(old, old + add * (1.0 - old)); // Blend towards 1.0
                
                if (nu > old) {
                zone.influences[i] = nu >= 1.0 ? 1.0 : nu;
                influencesChanged = true;
                }
            }
        }

        if (influencesChanged) {
            const updatedZones = zones.map((z) => (z.id === currentSelectedZoneId ? { ...z, influences: zone.influences } : z));
            setSmoothning({ zones: updatedZones });
        }
    }

    // ---- Smoothing Core -----------------------------------------------------
    function rebuildPreview() {
        const latest = useRectificationStore.getState().smoothning;
        const previewMesh = latest.previewMesh || ensurePreview();
        if (!previewMesh) return;

        const baseGeom = previewMesh.userData.baseGeometry;
        const basePos = baseGeom.attributes.position;
        const workGeom = previewMesh.geometry;
        const outPos = workGeom.attributes.position;
        const neighbors = previewMesh.userData.neighbors;
        const isBoundary = previewMesh.userData.isBoundary;
        const zones = latest.zones || [];
        const vertexCount = basePos.count;

        // Start from a fresh copy of the base positions for this rebuild.
        const curr = new Float32Array(basePos.array);
        // Create a temporary buffer to store the results of each iteration.
        const tmp = new Float32Array(curr.length);

        // This is the base strength of the smoothing effect for each iteration.
        const alpha = 1;

        // --- CORE SMOOTHING LOGIC ---
        // Loop over each user-defined zone.
        for (const zone of zones) {
            if (!zone.influences || zone.influences.length !== vertexCount) continue;
            
            // Get the number of smoothing iterations for this specific zone.
            const iters = Math.max(0, Math.round(parseFloat(zone.smootheningValue) || 0));
            if (iters === 0) continue;

            // Perform the smoothing for the specified number of iterations.
            for (let k = 0; k < iters; k++) {
                // Loop over every vertex in the mesh.
                for (let i = 0; i < vertexCount; i++) {
                    // Get the paint mask value (0 to 1) for this vertex in this zone.
                    const m = zone.influences[i] || 0;
                    const i3 = i * 3;
                    const ox = curr[i3 + 0], oy = curr[i3 + 1], oz = curr[i3 + 2];

                    // If vertex is on a boundary edge, keep its original position to preserve boundaries
                    if (isBoundary && isBoundary[i]) {
                        tmp[i3 + 0] = ox; tmp[i3 + 1] = oy; tmp[i3 + 2] = oz;
                        continue;
                    }

                    // If vertex is not influenced by this zone, keep its current position.
                    if (m <= 0.001) {
                        tmp[i3 + 0] = ox; tmp[i3 + 1] = oy; tmp[i3 + 2] = oz;
                        continue;
                    }

                    // Get the list of neighbors for the current vertex.
                    const nb = neighbors[i];
                    let sx = 0, sy = 0, sz = 0;
                    let cnt = 0;

                    // Sum the positions of all neighbors.
                    for (let j = 0; j < nb.length; j++) {
                        const jj = nb[j] * 3;
                        sx += curr[jj + 0]; sy += curr[jj + 1]; sz += curr[jj + 2];
                        cnt++;
                    }

                    // If there are neighbors, calculate their average position.
                    if (cnt === 0) {
                        tmp[i3 + 0] = ox; tmp[i3 + 1] = oy; tmp[i3 + 2] = oz;
                        continue;
                    }
                    const inv = 1 / cnt;
                    const ax = sx * inv, ay = sy * inv, az = sz * inv;
                    
                    // The blend factor is scaled by the paint mask strength.
                    const t = alpha * m;

                    // Blend the vertex's current position with the neighbors' average position.
                    tmp[i3 + 0] = ox * (1 - t) + ax * t;
                    tmp[i3 + 1] = oy * (1 - t) + ay * t;
                    tmp[i3 + 2] = oz * (1 - t) + az * t;
                }
                // After processing all vertices, swap the buffers for the next iteration.
                curr.set(tmp);
            }
        }

        // Write the final smoothed positions back to the preview geometry.
        outPos.array.set(curr);
        outPos.needsUpdate = true;
        workGeom.computeVertexNormals();
        workGeom.computeBoundingSphere?.();

        // Update overlays from influences
        const tmpColor = new THREE.Color();
        for (const zone of zones) {
            const overlayMesh = ensureZoneOverlay(zone.id);
            if (!overlayMesh || !zone.influences) continue;
            tmpColor.set(zone.color || "#ffffff").convertSRGBToLinear();
            overlayMesh.material.uniforms.uColor.value.copy(tmpColor);
            const paintAttr = overlayMesh.geometry.getAttribute("paint");
            paintAttr.array.set(zone.influences);
            paintAttr.needsUpdate = true;
        }
    }

    function initializeZoneInfluences(zones, vertexCount) {
        return zones.map((z) => {
            if (!z.influences || z.influences.length !== vertexCount) {
                return { ...z, influences: new Float32Array(vertexCount) };
            }
            return z;
        });
    }

    // ---- Effects ------------------------------------------------------------
    useEffect(() => {
        const zones = smoothning?.zones || [];
        const vertexCount = shadedMesh?.geometry.attributes.position.count;
        if (!vertexCount || zones.length === 0) return;
        const needsInit = zones.some((z) => !z.influences || z.influences.length !== vertexCount);
        if (needsInit) {
            const patched = initializeZoneInfluences(zones, vertexCount);
            setSmoothning({ zones: patched });
        }
    }, [smoothning?.zones, shadedMesh, setSmoothning]);

    useEffect(() => {
        if (shadedMesh && !shadedMesh.geometry.boundsTree) {
            shadedMesh.geometry.computeBoundsTree();
        }
    }, [shadedMesh]);

    useEffect(() => {
        if (!scene || !shadedMesh || !camera || !renderer) return;

        ensureOverlay();
        if (shadedMesh) setInteractiveObjects([shadedMesh]);
        ensurePreview();
        rebuildPreview();

        const dom = renderer.domElement;
        const ray = new THREE.Raycaster();
        const ndc = new THREE.Vector2();

        function intersect(ev) {
            const r = dom.getBoundingClientRect();
            ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
            ray.setFromCamera(ndc, camera);
            const hits = ray.intersectObject(shadedMesh, true);
            return hits[0] || null;
        }

        const onPointerDown = (ev) => {
            if (!ev.shiftKey || ev.button !== 0) return;
            const r = dom.getBoundingClientRect();
            ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
            ray.setFromCamera(ndc, camera);
            const hit = ray.intersectObject(shadedMesh, true)[0];
            if (!hit) return;
            paintingRef.current = true;
            if (controls) controls.enabled = false;
            lastPaintPosRef.current = null;
            paintAt(hit.point, false);
        };

        const onPointerMove = (ev) => {
            if (!ev.shiftKey) {
                if (useEditorStore.getState().paintingCursor.enabled) {
                    setPaintingCursor({ enabled: false });
                }
                return;
            }
            if (!useEditorStore.getState().paintingCursor.enabled) {
                setPaintingCursor({ enabled: true });
            }
            if (ev.buttons & 2 || !paintingRef.current) {
                return;
            }
            const hit = intersect(ev);

            const currentSelectedZoneId = getSelectedZoneId();
            if (hit && currentSelectedZoneId) {
                const z = (useRectificationStore.getState().smoothning?.zones || []).find((zz) => zz.id === currentSelectedZoneId);
                const color = z?.color || "#ffffff";
                const radiusWorld = (brushRadiusRef.current ?? 1) * CM_TO_UNIT;
                const canvas = renderer?.domElement;
                let radiusPx = Math.max(2, (brushRadiusRef.current ?? 1) * 16);
                if (canvas && camera) {
                    if (camera.isPerspectiveCamera) {
                        const fovRad = (camera.fov * Math.PI) / 180;
                        const distance = camera.position.distanceTo(hit.point);
                        const worldScreenHeight = 2 * distance * Math.tan(fovRad / 2);
                        const pxPerWorld = canvas.clientHeight / Math.max(worldScreenHeight, 1e-6);
                        radiusPx = Math.max(2, Math.round(radiusWorld * pxPerWorld));
                    } else if (camera.isOrthographicCamera) {
                        const effHeight = Math.abs((camera.top - camera.bottom) / Math.max(camera.zoom || 1, 1e-6));
                        const pxPerWorld = canvas.clientHeight / Math.max(effHeight, 1e-6);
                        radiusPx = Math.max(2, Math.round(radiusWorld * pxPerWorld));
                    }
                }
                setPaintingCursor({ enabled: true, radiusPx, color });
            } else {
                if (useEditorStore.getState().paintingCursor.enabled) {
                    setPaintingCursor({ enabled: false });
                }
            }

            if (!paintingRef.current) return;
            if (!hit) return;
            paintAt(hit.point, true); // Pass isDragging = true for move events
        };

        const onPointerUp = () => {
            paintingRef.current = false;
            lastPaintPosRef.current = null; // Reset for next stroke
            if (controls) controls.enabled = true;
        };

        const removeZoneOverlay = (zoneId) => {
            const overlay = overlayMeshRef.current[zoneId];
            if (overlay) {
                overlay.parent?.remove(overlay);
                overlay.material?.dispose();
                delete overlayMeshRef.current[zoneId];
            }
        };

        dom.addEventListener("pointerdown", onPointerDown);
        dom.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);

        return () => {
            dom.removeEventListener("pointerdown", onPointerDown);
            dom.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            if (controls) controls.enabled = true;
            if (shadedMesh) shadedMesh.visible = true;
            const zones = useRectificationStore.getState().smoothning?.zones || [];
            for (const zone of zones) removeZoneOverlay(zone.id);
            disposePreviewFromStore();
            setInteractiveObjects([]);
            setPaintingCursor({ enabled: false });
        };
    }, [scene, shadedMesh, camera, renderer, controls]);

    useEffect(() => {
        if (!shadedMesh) return;

        // Remove overlays for zones that no longer exist
        const currentZones = smoothning?.zones || [];
        const presentIds = new Set(currentZones.map((z) => z.id));
        for (const key in overlayMeshRef.current) {
            const id = Number(key);
            if (!presentIds.has(id)) {
                const overlay = overlayMeshRef.current[id];
                if (overlay) {
                    overlay.parent?.remove(overlay);
                    overlay.material?.dispose?.();
                    delete overlayMeshRef.current[id];
                }
            }
        }

        // Ensure overlays exist for all current zones and rebuild preview
        ensureOverlay();
        rebuildPreview();
    }, [smoothning?.zones]);

    useEffect(() => {
        const previewMesh = useRectificationStore.getState().smoothning?.previewMesh;
        if (previewMesh && shadedMesh) {
            const isPreviewVisible = (smoothning?.previewVisible && smoothning?.zones?.length > 0) ?? true;
            previewMesh.visible = isPreviewVisible;
            shadedMesh.visible = !isPreviewVisible;

            const zones = smoothning?.zones || [];
            for (const zone of zones) {
                const overlay = overlayMeshRef.current[zone.id];
                if (overlay) {
                    overlay.visible = !isPreviewVisible;
                }
            }
        }
    }, [smoothning?.previewVisible, shadedMesh, smoothning?.zones]);

    useEffect(() => {
        if (!shadedMesh || !(smoothning?.zones?.length > 0)) {
            setPaintingCursor({ enabled: false });
            return;
        }
        const z = (smoothning?.zones || []).find((zz) => zz.id === getSelectedZoneId());
        const color = z?.color || "#ffffff";
        setPaintingCursor({ enabled: true, color });
    }, [shadedMesh, smoothning?.zones, smoothning?.brushRadius]);

    return null;
};

export default SmootheningHandler;
